<template>
    <div class="border border-gray-400 px-4 py-2 rounded" v-if="addedText">
        <div class="flex items-center justify-between cursor-pointer"
             :class="textDropdownOpen ? 'mb-4' : ''"
             @click="textDropdownOpen = !textDropdownOpen">
            <div class="flex items-center space-x-2">
                <span v-if="textDropdownOpen"><IconChevronUp/></span>
                <span v-else><IconChevronDown/></span>
                <span class="border border-gray-400 rounded p-2"><IconAddText/></span>
                <h3 class="text-lg font-semibold text-gray-700">{{ textTitle }}</h3>
            </div>
            <button
                @click.stop="modalDeleteTextOpen = !modalDeleteTextOpen"
                class="text-gray-500 hover:text-red-500"
            >
                <IconTrash/>
            </button>
        </div>

        <div
            :style="{
            maxHeight: textDropdownOpen ? '500px' : '0',
            opacity: textDropdownOpen ? 1 : 0,
            pointerEvents: textDropdownOpen ? 'auto' : 'none',
            display: textDropdownOpen ? 'block' : 'none'
        }">
        <textarea
            v-model="textContent"
            @change="changedText"
            @keydown="handleKeydown"
            :placeholder="textPlaceholder"
            :class="[
                'w-full h-24 p-2 rounded-md outline-none',
                textValidation
                    ? 'border border-gray-200 focus:border-gray-400'
                    : 'border-2 border-red-500 focus:border-red-500'
            ]"
        />

            <div
                class="flex flex-col gap-2"
            >
                <div
                    class="flex items-center space-x-2"
                >
                    <select
                        v-model="fontFamily"
                        class="w-full max-w-4xl px-1 py-2 border border-gray-200 rounded-md"
                        :style="{ fontFamily: fontFamily }"
                    >
                        <option
                            v-for="font in fonts"
                            :key="font"
                            :value="font"
                            :style="{ fontFamily: font }"
                        >
                            {{ font }}
                        </option>
                    </select>
                    <select
                        v-model="fontSize"
                        class="w-20 px-1 py-2 border border-gray-200 rounded-md"
                    >
                        <option
                            v-for="size in fontSizes"
                            :key="size"
                        >
                            {{ size }}
                        </option>
                    </select>
                </div>
                <div
                    class="flex space-x-2"
                >
                    <div
                        class="w-1/2 flex"
                    >
                        <button
                            :class="buttonClass(alignment === 'left')"
                            class="w-1/3 rounded-l-md flex items-center justify-center"
                            @click="alignment = 'left'"
                        >
                            <IconAlignTextLeft
                                class="h-5 w-5"
                            />
                        </button>
                        <button
                            :class="buttonClass(alignment === 'center')"
                            class="w-1/3 flex items-center justify-center"
                            @click="alignment = 'center'"
                        >
                            <IconAlignTextCenter
                                class="h-5 w-5"
                            />
                        </button>
                        <button
                            :class="buttonClass(alignment === 'right')"
                            class="w-1/3 rounded-r-md flex items-center justify-center"
                            @click="alignment = 'right'"
                        >
                            <IconAlignTextRight
                                class="h-5 w-5"
                            />
                        </button>
                    </div>
                    <div
                        class="w-1/2"
                    >
                        <div>
                            <button
                                :class="buttonClass(isBold)"
                                class=" w-1/3 rounded-l-md"
                                @click="toggleBold"
                            >
                                <b>B</b>
                            </button>
                            <button
                                :class="buttonClass(isItalic)"
                                class="w-1/3"
                                @click="toggleItalic"
                            >
                                <i>I</i>
                            </button>
                            <button
                                :class="buttonClass(isUnderline)"
                                class="w-1/3 rounded-r-md"
                                @click="toggleUnderline"
                            >
                                <u>I</u>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="relative">
                    <button
                        v-if="!forcedColor"
                        @click="toggleDropdown"
                        class="flex items-center justify-between w-full border border-gray-300 rounded p-2 text-sm"
                    >
                        <div class="flex items-center space-x-2">
                            <div
                                class="w-5 h-5 rounded-full border border-gray-300"
                                :style="{ backgroundColor: selectedColor.hex }"
                            ></div>
                            <span>{{ selectedColor.name }}</span>
                        </div>
                        <span v-if="dropdownOpen"><IconChevronUp/></span>
                        <span v-else><IconChevronDown/></span>
                    </button>
                    <div
                        v-else
                        class="flex items-center justify-between w-full border border-gray-300 rounded p-2 text-sm bg-gray-50 cursor-not-allowed"
                    >
                        <div class="flex items-center space-x-2">
                            <div
                                class="w-5 h-5 rounded-full border border-gray-300"
                                :style="{ backgroundColor: selectedColor.hex }"
                            ></div>
                            <span>{{ selectedColor.name }}</span>
                        </div>
                    </div>

                    <div
                        v-if="dropdownOpen && !forcedColor"
                        class="absolute left-0 z-50 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg"
                    >
                        <div class="p-2 border-b border-gray-200">
                            <input
                                v-model="colorFilterText"
                                type="text"
                                :placeholder="filterColorPlaceholder"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                        </div>
                        <div class="h-[200px] overflow-y-auto">
                            <div v-if="loadingColors" class="flex items-center justify-center py-8">
                                <span class="text-gray-500 text-sm">{{ loadingColorsText }}</span>
                            </div>
                            <div v-else-if="filteredColors.length === 0" class="flex items-center justify-center py-8">
                                <span class="text-gray-500 text-sm">{{ noColorsAvailableText }}</span>
                            </div>
                            <ul v-else class="space-y-1">
                                <li
                                    v-for="color in filteredColors"
                                    :key="color.hex"
                                    @click="selectColor(color)"
                                    class="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                                >
                                    <div
                                        class="w-5 h-5 rounded-full border border-gray-300 mr-2"
                                        :style="{ backgroundColor: color.hex }"
                                    ></div>
                                    <div class="flex-1">
                                        <div class="text-sm font-semibold">{{ color.name }}</div>
                                        <div class="text-xs text-gray-500">{{ color.hex }}</div>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <Modal
        v-model="modalDeleteTextOpen"
        :title="deleteLayerTitle"
        :size="'sm'">
        <div
            class="flex items-center mb-4 mt-8"
        >
            <div
                class="text-sm mt-3"
            >
                <p>{{ deleteLayerMessage }}</p>
            </div>
        </div>
        <div
            class="flex space-x-4 w-full mt-8"
        >
            <button
                class="w-1/2 px-4 py-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                @click="modalDeleteTextOpen = false"
            >
                {{ cancelText }}
            </button>
            <button
                class="w-1/2 px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-600"
                @click="removeTextLayer"
            >
                {{ deleteText }}
            </button>
        </div>
    </Modal>
</template>

<script setup>
import {
    ref, computed, watch, reactive, onMounted
} from 'vue'

import IconAlignTextCenter from "../icons/IconAlignTextCenter.vue";
import IconAlignTextRight from "../icons/IconAlignTextRight.vue";
import IconAlignTextLeft from "../icons/IconAlignTextLeft.vue";
import IconChevronDown from "../icons/IconChevronDown.vue";
import IconChevronUp from "../icons/IconChevronUp.vue";
import IconAddText from "../icons/IconAddText.vue";
import IconTrash from "../icons/IconTrash.vue";
import Modal from "../Modal.vue";
import {useTranslation} from '../../composables/useTranslation.js';
import {VISUALIZER_FONTS} from '../../utils/visualizerFonts';


const props = defineProps({
    removeText: {
        type: Number,
        default: 0, // Wartość domyślna
    },
    removeTransformerSelection: {
        type: Function,
        default: null,
    },
    revertFontSize: {
        type: Number,
        default: null,
    },
    revertTextContent: {
        type: String,
        default: null,
    },
    textValidation: {
        type: Boolean,
        default: true,
    },
    addedText: {
        type: Boolean,
        default: false,
    },
    forcedColor: {
        type: Object,
        default: null,
    },
})

watch(
    () => props.removeText, // Obiekt lub funkcja obserwowana
    (newValue, oldValue) => {
        removeTextLayer();
    }
);
watch(
    () => props.revertFontSize, // Obiekt lub funkcja obserwowana
    (newValue, oldValue) => {
        fontSize.value = newValue
    }
);

watch(
    () => props.revertTextContent,
    (newValue, oldValue) => {
        if (newValue !== null && newValue !== undefined) {
            textContent.value = newValue
        }
    }
);

const DEFAULT_TEXT_CONTENT = 'Tekst';
const DEFAULT_FONT_FAMILY = 'Times New Roman';
const DEFAULT_FONT_SIZE = 14;
const DEFAULT_ALIGNMENT = 'left';
const DEFAULT_COLOR_HEX = '#000000';
const DEFAULT_COLOR_NAME = 'Pure Black';

function resetToDefaults() {
    textContent.value = DEFAULT_TEXT_CONTENT;
    fontFamily.value = DEFAULT_FONT_FAMILY;
    fontSize.value = DEFAULT_FONT_SIZE;
    alignment.value = DEFAULT_ALIGNMENT;
    isBold.value = false;
    isItalic.value = false;
    isUnderline.value = false;
    textDropdownOpen.value = true;
    if (props.forcedColor) {
        selectedColor.name = props.forcedColor.name || props.forcedColor.pantone;
        selectedColor.hex = props.forcedColor.hex;
        textColor.value = props.forcedColor.hex;
    } else {
        selectedColor.name = DEFAULT_COLOR_NAME;
        selectedColor.hex = DEFAULT_COLOR_HEX;
        textColor.value = DEFAULT_COLOR_HEX;
    }
}

watch(
    () => props.addedText,
    (newValue) => {
        if (newValue !== true) return;
        if (!textContent.value || textContent.value.trim() === '') {
            resetToDefaults();
        }
        if (props.forcedColor) {
            selectedColor.name = props.forcedColor.name || props.forcedColor.pantone;
            selectedColor.hex = props.forcedColor.hex;
            textColor.value = props.forcedColor.hex;
        }
        const config = textConfiguration.value;
        emit('update:textLayer', config);
        emit('update:textContent', textContent.value);
        setTimeout(() => {
            emit('update:textLayer', { ...config });
        }, 0);
    }
);

watch(
    () => props.forcedColor,
    (newVal) => {
        if (newVal && props.addedText) {
            selectedColor.name = newVal.name || newVal.pantone;
            selectedColor.hex = newVal.hex;
            textColor.value = newVal.hex;
        }
    },
    { immediate: true, deep: true }
);

const {$t} = useTranslation()
const textContent = ref('Tekst')
const fontFamily = ref('Times New Roman')
const fontSize = ref(14)
const alignment = ref('left')
const isBold = ref(false)
const isItalic = ref(false)
const isUnderline = ref(false)
const textColor = ref('#000000')
const textDropdownOpen = ref(true)
const modalDeleteTextOpen = ref(false)

const textTitle = ref('')
const textPlaceholder = ref('')
const deleteLayerTitle = ref('')
const deleteLayerMessage = ref('')
const cancelText = ref('')
const deleteText = ref('')
const allColors = ref([])
const loadingColors = ref(false)
const colorFilterText = ref('')
const filterColorPlaceholder = ref('')
const loadingColorsText = ref('')
const noColorsAvailableText = ref('')

onMounted(async () => {
    textTitle.value = await $t('text.title', 'Tekst')
    textPlaceholder.value = await $t('text.placeholder', 'Twój tekst tutaj...')
    deleteLayerTitle.value = await $t('text.delete_layer_title', 'Usuwanie warstwy tekstowej')
    deleteLayerMessage.value = await $t('text.delete_layer_message', 'Czy na pewno chcesz usunąć warstwę tekstową z wizualizacji?')
    cancelText.value = await $t('text.cancel', 'Anuluj')
    deleteText.value = await $t('text.delete', 'Usuń')
    filterColorPlaceholder.value = await $t('colors.filter_placeholder', 'Filtruj po nazwie Pantone...')
    loadingColorsText.value = await $t('colors.loading_colors', 'Ładowanie kolorów...')
    noColorsAvailableText.value = await $t('colors.no_colors_available', 'Brak dostępnych kolorów')
})

const availableColors = computed(() => {
    if (allColors.value.length === 0) {
        return [
            {
                name: 'Pure Black',
                hex: '#000000'
            },
            {
                name: 'Pure White',
                hex: '#FFFFFF'
            }
        ];
    }
    return allColors.value;
});

const filteredColors = computed(() => {
    if (!colorFilterText.value.trim()) {
        return availableColors.value;
    }

    const filterLower = colorFilterText.value.toLowerCase().trim();
    return availableColors.value.filter(color =>
        color.name && color.name.toLowerCase().includes(filterLower)
    );
});

const selectedColor = reactive({name: "Pure Black", hex: "#000000"})
const dropdownOpen = ref(false)

const toggleDropdown = async () => {
    dropdownOpen.value = !dropdownOpen.value;

    if (dropdownOpen.value && allColors.value.length === 0) {
        await loadAllColors();
    }
}

const loadAllColors = async () => {
    if (allColors.value.length > 5) {
        return;
    }
    loadingColors.value = true;
    try {
        const response = await fetch("/api/file/all-colors");
        if (!response.ok) {
            throw new Error('Błąd podczas pobierania kolorów');
        }
        const data = await response.json();
        allColors.value = (data.colors || []).map(color => ({
            name: color.pantone,
            hex: color.hex
        }));

        if (selectedColor.name === '' && selectedColor.hex) {
            selectedColor.name = allColors.value.find(color => color.hex === selectedColor.hex)?.name;
        }
    } catch (error) {
        console.error('Błąd podczas ładowania kolorów:', error);
        allColors.value = [];
    } finally {
        loadingColors.value = false;
    }
};

const selectColor = (color) => {
    selectedColor.name = color.name;
    selectedColor.hex = color.hex;
    textColor.value = color.hex;
    dropdownOpen.value = false;
    colorFilterText.value = '';
}

const fonts = VISUALIZER_FONTS
const fontSizes = [10, 12, 14, 16, 20, 24, 32, 48, 64, 72, 96]

const textConfiguration = computed(() => ({
    fontFamily: fontFamily.value,
    fontSize: `${fontSize.value}px`,
    textAlign: alignment.value,
    fontWeight: isBold.value ? 'bold' : 'normal',
    fontStyle: isItalic.value ? 'italic' : 'normal',
    textDecoration: isUnderline.value ? 'underline' : 'none',
    color: textColor.value,
    pantone: selectedColor.name,
}))

const buttonClass = (isActive) => `px-2 py-1 border border-gray-200 ${isActive ? 'bg-gray-300' : 'bg-white-100'}`

const toggleBold = () => (isBold.value = !isBold.value)
const toggleItalic = () => (isItalic.value = !isItalic.value)
const toggleUnderline = () => (isUnderline.value = !isUnderline.value)

const handleKeydown = (event) => {
    if (!props.textValidation) {
        const allowedKeys = [
            'Backspace',
            'Delete',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End',
            'Tab'
        ];

        if (!allowedKeys.includes(event.key)) {
            event.preventDefault();
        }
    }
}

const removeTextLayer = () => {
    props.removeTransformerSelection();
    modalDeleteTextOpen.value = false
    textDropdownOpen.value = false
    textContent.value = '';
    emit('remove:textLayer')
    emit('update:textContent', textContent.value)
}
const getCurrentPantone = () => {
    return selectedColor.name;
}

const initFromVisualization = (visualization) => {
    if (visualization && visualization.text_config && visualization.text_config.visible) {
        let textConfig = visualization.text_config;
        fontFamily.value = textConfig.fontFamily;
        fontSize.value = textConfig.fontSize;
        alignment.value = textConfig.align;
        isBold.value = textConfig.fontWeight === 'bold';

        const fontStyle = (textConfig.fontStyle || '').toLowerCase();
        isItalic.value = fontStyle.includes('italic');

        isUnderline.value = textConfig.textDecoration === 'underline';
        textColor.value = textConfig.fill;
        textContent.value = textConfig.text;


        selectedColor.name = allColors.value.length > 0 ? allColors.value.find(color => color.hex === textConfig.fill)?.name : '';
        selectedColor.hex = textConfig.fill;

        emit('update:textLayer', textConfiguration.value)
        emit('update:textContent', textContent.value)


    }
}

watch(
    textConfiguration,
    (newConfig) => {
        if (props.addedText) {
            emit('update:textLayer', newConfig)
        }
    },
    {deep: true},
)

watch(
    textContent,
    (newConfig) => {
        if (props.addedText) {
            emit('update:textContent', newConfig)
        }

    },
    {deep: true},
)

const emit = defineEmits([
    'remove:textLayer',
    'update:textLayer',
    'update:textContent'
])

defineExpose({
    getCurrentPantone,
    initFromVisualization
})


onMounted((async () => {
    loadAllColors();
    if (props.addedText) {
        emit('update:textLayer', textConfiguration.value)
        emit('update:textContent', textContent.value)

    }

}))
</script>

<style scoped>

</style>
